import Chat16 from '@carbon/icons-react/es/chat/16';
import Settings16 from '@carbon/icons-react/es/settings/16';
import Tools16 from '@carbon/icons-react/es/tools/16';
import { Tabs } from 'antd';
import React, { FC, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { TemplateId } from 'api/models';
import { ReactComponent as FormTemplateAutomationSvg } from 'assets/svgs/form-automation.svg';
import { PageLoading } from 'components/atoms/loading';
import { CompactMenuTitle } from 'components/atoms/menu/compact-menu';
import { NotFound } from 'components/atoms/result';
import { Page } from 'components/molecules/page';
import { VariablesContext } from 'contexts/variables';
import { useFormTemplatesQuery } from 'queries/form-template';
import { FormAutomation } from './form-automation';
import { FormBuilder } from './form-builder';
import { FormPageHeader } from './form-page-header';
import { FormPanePreview } from './form-pane-preview';
import { FormTemplateResponsesTable } from './form-responses';
import { FormSettings } from './form-settings';
import { constructTreeStructure } from './utils';

const FormTabKeys = {
  FormBuilder: 'form_builder',
  FormAutomations: 'form_automations',
  FormResponses: 'form_responses',
  FormSettings: 'form_settings',
};

const StyledTabs = styled(Tabs)`
  & > .ant-tabs-nav {
    margin-bottom: 2px;
    box-shadow: var(--shadow-02);
    & > .ant-tabs-nav-wrap {
      background: var(--coolest-gray-10);
      & > .ant-tabs-nav-list {
        padding: 0px var(--left-spacing);
        & > .ant-tabs-tab {
          padding: 8px 0;
          min-width: 0;
          margin-right: 16px !important;
          border-bottom: 2px solid transparent;
        }
        & > .ant-tabs-tab:hover {
          border-bottom-color: var(--cool-gray-50);
        }
      }
    }
  }

  & > .ant-tabs-content-holder {
    height: calc(100vh - 135px);
    & > .ant-tabs-content {
      height: 100%;
    }
  }
`;

const FormResponseWrapper = styled.div`
  width: 100%;
  height: 100%;
  overflow: scroll;
`;
const { TabPane } = Tabs;

export const FormTemplateDetailsPage: FC = () => {
  const { templateId } = useParams<{ templateId: string }>();

  const { data: formTemplate, isLoading: isFormTemplateLoading } =
    useFormTemplatesQuery(TemplateId(parseInt(templateId, 10)));

  const [showFormPane, setShowFormPane] = useState(false);
  const transformedFormTemplate = useMemo(() => {
    if (!formTemplate) {
      return null;
    }

    const updatedFormQuestions = constructTreeStructure(
      formTemplate.formQuestions,
    );

    return {
      ...formTemplate,
      formQuestions: updatedFormQuestions,
    };
  }, [formTemplate]);

  return (
    <Page>
      {isFormTemplateLoading ? (
        <PageLoading />
      ) : transformedFormTemplate ? (
        <>
          <FormPageHeader
            formTemplate={transformedFormTemplate}
            setShowFormPane={setShowFormPane}
          />
          <VariablesContext.Provider
            value={{
              allowSimpleVariables: true,
              resolveVariablesToFallback: true,
            }}
          >
            <FormPanePreview
              formTemplate={transformedFormTemplate}
              showFormPane={showFormPane}
              setShowFormPane={setShowFormPane}
            />
          </VariablesContext.Provider>

          <Page.Content>
            <StyledTabs
              defaultActiveKey={FormTabKeys.FormBuilder}
              tabBarGutter={1}
            >
              <TabPane
                tab={
                  <CompactMenuTitle
                    icon={<Tools16 />}
                    text={<FormattedMessage id="form_templates.builder" />}
                  />
                }
                key={FormTabKeys.FormBuilder}
              >
                <FormBuilder formTemplate={transformedFormTemplate} />
              </TabPane>
              <TabPane
                tab={
                  <CompactMenuTitle
                    icon={<Chat16 />}
                    text={<FormattedMessage id="form_templates.responses" />}
                  />
                }
                key={FormTabKeys.FormResponses}
              >
                <FormResponseWrapper>
                  <FormTemplateResponsesTable
                    formTemplateId={transformedFormTemplate.templateId}
                  />
                </FormResponseWrapper>
              </TabPane>
              <TabPane
                tab={
                  <CompactMenuTitle
                    icon={<FormTemplateAutomationSvg />}
                    text={<FormattedMessage id="form_templates.automations" />}
                  />
                }
                key={FormTabKeys.FormAutomations}
              >
                <FormAutomation formTemplate={transformedFormTemplate} />
              </TabPane>
              <TabPane
                tab={
                  <CompactMenuTitle
                    icon={<Settings16 />}
                    text={<FormattedMessage id="settings" />}
                  />
                }
                key={FormTabKeys.FormSettings}
              >
                <FormSettings formTemplate={transformedFormTemplate} />
              </TabPane>
            </StyledTabs>
          </Page.Content>
        </>
      ) : (
        <NotFound />
      )}
    </Page>
  );
};
